import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

import { apiFetch } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const GREETING = "Namaste! I am the Laxwedeepan concierge. Ask me about our ghee, oils, kapoor, clay friends or festive specials.";

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const data = await apiFetch("/api/chat", {
        // Longer than the server's 20s LLM timeout so its friendly 503 message arrives first
        timeout: 25000,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, history: next.slice(-8) }),
      });
      setMessages([...next, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages([...next, { role: "assistant", content: err.message || "Our concierge is resting right now. Please WhatsApp us at +91 92055 11502." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="chat-toggle-button"
        aria-label="Open the Laxwedeepan concierge chat"
        className="fixed bottom-24 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full border border-brand-gold bg-brand-ivory text-brand-maroon shadow-lg transition-transform duration-300 hover:scale-110"
      >
        {open ? <X className="h-5 w-5" strokeWidth={1.5} /> : <MessageCircle className="h-5 w-5" strokeWidth={1.5} />}
      </button>
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-40 right-6 z-50 flex w-[21rem] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-sm border border-brand-border bg-brand-ivory shadow-xl"
        >
          <div className="flex items-center gap-2 border-b border-brand-border bg-brand-cream/60 px-5 py-4">
            <Sparkles className="h-4 w-4 text-brand-gold" strokeWidth={1.5} />
            <p className="font-display text-lg text-brand-ink">Laxwedeepan Concierge</p>
          </div>
          <div ref={listRef} className="flex h-72 flex-col gap-3 overflow-y-auto px-4 py-4" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`max-w-[85%] rounded-sm px-4 py-2.5 text-sm font-light leading-relaxed ${
                  m.role === "user"
                    ? "self-end bg-brand-maroon text-brand-ivory"
                    : "self-start border border-brand-border bg-white text-brand-ink/80"
                }`}
              >
                {m.content}
              </div>
            ))}
            {busy && <p className="self-start text-xs italic text-brand-ink/40">typing…</p>}
          </div>
          <div className="flex items-center gap-2 border-t border-brand-border px-3 py-3">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              data-testid="chat-input"
              placeholder="Ask about our offerings…"
              className="flex-1 bg-transparent px-2 text-sm font-light text-brand-ink outline-none placeholder:text-brand-ink/35"
            />
            <button
              onClick={send}
              disabled={busy}
              data-testid="chat-send-button"
              aria-label="Send message"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-maroon text-brand-ivory transition-colors duration-300 hover:bg-brand-gold disabled:opacity-50"
            >
              <Send className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
